import { FeatureMiddleware } from '@cast/api-client';
import {
  demoClusterDetails,
  demoClusterNodeList,
  demoClusterNode,
} from '@cast/fixtures';
import { Node } from '@cast/types';

import { getAbility } from '@cast/infrastructure-abilities';
import {
  getIsClusterDemoMode,
  getIsDemoMode,
  getIsOpDemoMode,
  getIsSecurityDemoMode,
  setHasClusters,
} from '@cast/infrastructure-demo';
import { TourSteps } from 'features/demo/types';

import { useRecorderState } from '../recorder';
import {
  getOrgId,
  rejectDemoClusterRequest,
  getTourStepIdFromUrl,
  isSimulatedDemoClusterRequest,
  opDemoLoader,
} from '../utils';

export const clusters: FeatureMiddleware<'clusters'> = {
  getClusters: async ({ args, next, resolve, feature, endpoint }) => {
    const response = await next();
    const recorderState = useRecorderState.getState();
    let clusters = response.data?.items ?? [];

    const ability = getAbility();
    if (ability) {
      const clustersWithPermissions = clusters.filter((c) =>
        ability.can('view', 'Clusters', { clusterId: c.id })
      );
      // Set raw fact
      setHasClusters(clustersWithPermissions.length > 0);
      clusters = clustersWithPermissions;
    }

    if (recorderState.isActive) {
      recorderState.recordData({
        feature,
        endpoint,
        record: { data: response.data, parameters: args },
      });
      return resolve({ items: clusters }, response);
    }

    if (getIsOpDemoMode()) {
      const fixture = await opDemoLoader.loadFixture({
        feature,
        endpoint,
      });
      if (fixture) {
        return resolve(fixture);
      }
    }

    // Cluster demo mode fallback - only use if no real clusters exist
    if (getIsClusterDemoMode()) {
      const orgId = getOrgId();
      const demoCluster = await demoClusterDetails(orgId);
      return resolve({
        items: demoCluster ? [demoCluster] : [],
      });
    }
    // Return only clusters, that the user can view. This was moved from query `select`
    return resolve({ items: clusters }, response);
  },
  getCluster: async ({ next, resolve }) => {
    // OP demo mode: load getClusters fixture and extract first cluster
    if (getIsOpDemoMode()) {
      const getClustersFixture = await opDemoLoader.loadFixture({
        feature: 'clusters',
        endpoint: 'getClusters',
      });
      if (getClustersFixture?.items?.[0]) {
        return resolve(getClustersFixture.items[0]);
      }
    }

    // Cluster demo mode fallback
    if (getIsClusterDemoMode()) {
      const fixture = await demoClusterDetails(getOrgId());
      return resolve(fixture);
    }

    return next();
  },
  getNodes: async ({ args, next, resolve, orig, feature, endpoint }) => {
    if (getIsOpDemoMode()) {
      const fixture = await opDemoLoader.loadFixture({
        // User may be freely browser the non-cluster features (e.g. security), while there is only an OP demo cluster returned
        state: getTourStepIdFromUrl() || TourSteps.ObserveEvictorOnNodes,
        feature,
        endpoint,
        endpointParameters: args,
      });
      if (fixture) {
        return resolve(fixture);
      }
    }

    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoClusterNodeList();
      return resolve(fixture);
    }

    const [endpointArgs = {} as (typeof args)[0], ...rest] = args;

    if (endpointArgs?.pageLimit) {
      return next();
    }
    // TODO (Justas): This is moved from old implementation. Move to the separate middleware
    const aggregatedNodes: Node[] = [];
    let lastResponse: Awaited<ReturnType<typeof orig>>;
    let nextCursor: string | undefined;

    while (true) {
      lastResponse = await orig(
        { ...endpointArgs, pageCursor: nextCursor, pageLimit: '3000' },
        ...rest
      );
      if (lastResponse?.data?.items) {
        aggregatedNodes.push(...(lastResponse.data.items as Node[]));
      }
      if (!lastResponse.data.nextCursor) {
        break;
      }
      nextCursor = lastResponse.data.nextCursor;
    }

    lastResponse.data.items = aggregatedNodes;

    const recorderState = useRecorderState.getState();

    if (recorderState.isActive) {
      recorderState.recordData({
        feature,
        endpoint,
        record: { data: lastResponse.data, parameters: args },
      });
    }

    return lastResponse;
  },
  getNode: async ({ args, next, resolve }) => {
    if (getIsOpDemoMode()) {
      return resolve({} as Node);
    }
    if (getIsClusterDemoMode() || isSimulatedDemoClusterRequest(args[0])) {
      const fixture = await demoClusterNode(args[0]?.nodeId);
      return resolve(fixture);
    }
    return next();
  },
  getCredentialsScript: async ({ next }) => {
    // Allow in security demo mode so users can see the connect script
    if (!getIsSecurityDemoMode() && getIsClusterDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  getOffboardingScript: async ({ next }) => {
    if (getIsDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  events: async ({ next }) => {
    if (getIsDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  updateCluster: async ({ next }) => {
    if (getIsDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  deleteCluster: async ({ next }) => {
    if (getIsDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  disconnectCluster: async ({ next }) => {
    if (getIsDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
  deleteNode: async ({ next }) => {
    if (getIsDemoMode()) {
      return rejectDemoClusterRequest();
    }
    return next();
  },
};
